// Ported from genieos/web/src/components/historian/TimeRangeSelector.tsx @ 84923d036
/**
 * TimeRangeSelector Component
 *
 * Time range selection with preset buttons and custom date/time input.
 * Supports keyboard navigation and accessibility.
 *
 * The custom-range inputs are `datetime-local`, which carry no timezone: their
 * value is LOCAL wall-clock text both ways. Seeding and reading them MUST go
 * through the symmetric pair in @/utils/datetimeLocal — a formatter that renders
 * UTC into a field parsed as local shifted the range by the browser's offset on
 * every Apply, compounding each time (Demsv1.0#3797).
 *
 * Presets come from `TIME_RANGE_PRESETS` in `historianConstants` — the shared
 * definition the context's `setTimeRangePreset` also consumes — rather than a
 * second local list. genieos additionally offered a `last_5m` preset because its
 * high-rate supertables (synchrophasor, pcs_hf_telemetry) are capped server-side
 * at a 5-minute window; this repo has no TDengine supertables, so that preset
 * does not apply here and is deliberately omitted (djin-cloud#806).
 *
 * §5.4 CLIENT-SIDE RANGE GUARD: `MAX_RANGE_DAYS` mirrors the server's
 * `MAX_RANGE_MS` (`historian-series.service.ts`, also 366 days, also a strict
 * `>` comparison). The server is the enforcer — this is here so a custom range
 * that is too long is rejected inline instead of round-tripping to a 400.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Clock, Calendar, ChevronDown, Check } from 'lucide-react';
import { useHistorian } from '@/contexts/HistorianContext';
import { TIME_RANGE_PRESETS, MAX_RANGE_DAYS, type TimeRangePreset } from './historianConstants';
import { cn } from '@/utils/cn';
import { formatDateTimeLocal, parseDateTimeLocal } from '@/utils/datetimeLocal';
import { Button, Card } from '@/components/gamma';

interface TimeRangeSelectorProps {
  className?: string;
}

interface PresetOption {
  value: TimeRangePreset;
  label: string;
}

const PRESETS: PresetOption[] = [
  ...TIME_RANGE_PRESETS.map((p) => ({ value: p.id, label: p.label })),
  { value: 'custom', label: 'Custom' },
];

const ONE_DAY_MS = 86400e3;

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  className,
}) => {
  const { state, setTimeRange, setTimeRangePreset } = useHistorian();
  const { timeRange } = state;

  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(timeRange.preset === 'custom');

  // Custom date inputs
  const [customStart, setCustomStart] = useState(
    formatDateTimeLocal(timeRange.start)
  );
  const [customEnd, setCustomEnd] = useState(
    formatDateTimeLocal(timeRange.end)
  );

  // Inline validation hint for the custom range inputs (§5.4). Cleared on any
  // edit so a stale message never survives past the input it described.
  const [rangeHint, setRangeHint] = useState<string | null>(null);

  // Extracted so the effect below can depend on primitive values instead of
  // `timeRange` object identity — and so `react-hooks/exhaustive-deps` can
  // statically verify the dependency array (it flags an inline `.getTime()`
  // call in a deps array as "too complex to check").
  const timeRangeStartMs = timeRange.start.getTime();
  const timeRangeEndMs = timeRange.end.getTime();

  // Update custom inputs when time range changes externally.
  //
  // ⚠️ Deliberately DIFFERENT from genieos's original, which keyed this on
  // `[timeRange]` (the whole object). `state.timeRange` here can carry a
  // fresh identity on every `executeQuery` for a duration preset — the
  // context re-resolves the window at execute time, since a stored
  // `last_1h` means "the hour before THIS execute" — so an object-identity
  // dependency re-runs this effect, and its four setters, on every query,
  // not just on a genuine range change. Keying on the primitive values means
  // it only fires when the range actually moved. It also matters for
  // `rangeHint`: keyed on the object, an execute would clear the hint even
  // though the range never changed; keyed on the values, the hint's lifetime
  // correctly tracks the range it describes.
  useEffect(() => {
    setCustomStart(formatDateTimeLocal(new Date(timeRangeStartMs)));
    setCustomEnd(formatDateTimeLocal(new Date(timeRangeEndMs)));
    setShowCustom(timeRange.preset === 'custom');
    setRangeHint(null);
  }, [timeRange.preset, timeRangeStartMs, timeRangeEndMs]);

  // Format date for display
  function formatDisplayDate(date: Date): string {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Handle preset selection
  const handlePresetClick = useCallback(
    (preset: TimeRangePreset) => {
      if (preset === 'custom') {
        setShowCustom(true);
      } else {
        setShowCustom(false);
        setRangeHint(null);
        setTimeRangePreset(preset);
      }
      setIsOpen(false);
    },
    [setTimeRangePreset]
  );

  // Handle custom range apply
  //
  // Every rejection path sets `rangeHint`, not just the §5.4 oversize guard —
  // a control that silently ignores the click on the other two paths is worse
  // than one that refuses loudly, since the operator cannot tell "rejected"
  // from "broken" (CodeRabbit finding on #813).
  const handleApplyCustom = useCallback(() => {
    const start = parseDateTimeLocal(customStart);
    const end = parseDateTimeLocal(customEnd);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setRangeHint('Enter a valid start and end date/time.');
      return;
    }

    if (end <= start) {
      setRangeHint('End must be after start.');
      return;
    }

    // §5.4: reject client-side, before the server ever sees the request. Strict
    // `>` mirrors historian-series.service.ts's MAX_RANGE_MS check exactly, so a
    // range of precisely MAX_RANGE_DAYS is accepted on both sides.
    if (end.getTime() - start.getTime() > MAX_RANGE_DAYS * ONE_DAY_MS) {
      setRangeHint(`Custom range cannot exceed ${MAX_RANGE_DAYS} days.`);
      return;
    }

    setRangeHint(null);
    setTimeRange({
      preset: 'custom',
      start,
      end,
    });
  }, [customStart, customEnd, setTimeRange]);

  // Get current preset label
  const currentPreset = PRESETS.find((p) => p.value === timeRange.preset);
  const displayLabel = showCustom
    ? `${formatDisplayDate(timeRange.start)} - ${formatDisplayDate(timeRange.end)}`
    : currentPreset?.label || 'Select time range';

  return (
    <Card variant="default" className={cn('p-4', className)}>
      <div className="space-y-4">
        {/* Header with dropdown */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ems-text-primary flex items-center gap-2">
            <Clock className="w-4 h-4 text-ems-blue" />
            Time Range
          </h3>
        </div>

        {/* Preset dropdown */}
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg',
              'bg-ems-bg-card-hover border border-ems-border',
              'text-ems-text-primary text-sm',
              'hover:bg-ems-bg-card-hover transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-ems-blue/50'
            )}
          >
            <span className="truncate">{displayLabel}</span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-ems-text-secondary transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </button>

          {/* Dropdown menu */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 py-1 bg-ems-bg-card border border-ems-border rounded-lg shadow-xl">
              {PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => handlePresetClick(preset.value)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 text-sm',
                    'hover:bg-ems-bg-card-hover transition-colors',
                    timeRange.preset === preset.value &&
                      !showCustom &&
                      'text-ems-blue',
                    showCustom &&
                      preset.value === 'custom' &&
                      'text-ems-blue'
                  )}
                >
                  <span>{preset.label}</span>
                  {((timeRange.preset === preset.value && !showCustom) ||
                    (showCustom && preset.value === 'custom')) && (
                    <Check className="w-4 h-4" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Preset quick buttons */}
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.filter((p) => p.value !== 'custom').map((preset) => (
            <Button
              key={preset.value}
              variant={
                timeRange.preset === preset.value && !showCustom
                  ? 'primary'
                  : 'secondary'
              }
              size="xs"
              onClick={() => handlePresetClick(preset.value)}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        {/* Custom range inputs */}
        {showCustom && (
          <div className="space-y-3 pt-2 border-t border-ems-border">
            <div>
              <label className="block text-xs text-ems-text-secondary mb-1.5">
                Start Date/Time
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ems-text-secondary" />
                <input
                  type="datetime-local"
                  value={customStart}
                  onChange={(e) => {
                    setCustomStart(e.target.value);
                    setRangeHint(null);
                  }}
                  className={cn(
                    'w-full pl-9 pr-3 py-2 rounded-lg',
                    'bg-ems-bg-card-hover border border-ems-border',
                    'text-ems-text-primary text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ems-blue/50'
                  )}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-ems-text-secondary mb-1.5">
                End Date/Time
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ems-text-secondary" />
                <input
                  type="datetime-local"
                  value={customEnd}
                  onChange={(e) => {
                    setCustomEnd(e.target.value);
                    setRangeHint(null);
                  }}
                  className={cn(
                    'w-full pl-9 pr-3 py-2 rounded-lg',
                    'bg-ems-bg-card-hover border border-ems-border',
                    'text-ems-text-primary text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ems-blue/50'
                  )}
                />
              </div>
            </div>

            {rangeHint && (
              <p className="text-xs text-ems-red" role="alert">
                {rangeHint}
              </p>
            )}

            <Button
              variant="primary"
              size="sm"
              onClick={handleApplyCustom}
              className="w-full"
            >
              Apply Custom Range
            </Button>
          </div>
        )}

        {/* Current range display */}
        <div className="pt-2 border-t border-ems-border">
          <div className="text-xs text-ems-text-secondary space-y-1">
            <div className="flex justify-between">
              <span>From:</span>
              <span className="text-ems-text-primary">
                {timeRange.start.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>To:</span>
              <span className="text-ems-text-primary">
                {timeRange.end.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
